import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Sparkle, ArrowLeft, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useFirebaseAuth } from "../context/FirebaseAuthContext";
import { db } from "../lib/firebase";
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp, getDoc } from "firebase/firestore";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card } from "../components/ui/card";
import {Filter} from "lucide-react";

const MentorsPage = () => {
    const { user, userData } = useFirebaseAuth();
    const navigate = useNavigate();
    const [mentors, setMentors] = useState([]);
    const [disciplines, setDisciplines] = useState([]);
    const [selectedDiscipline, setSelectedDiscipline] = useState('');
    const [loading, setLoading] = useState(true);
    const [requestingMentor, setRequestingMentor] = useState(null);

    useEffect(() => {
        fetchDisciplines();
    }, []);

    useEffect(() => {
        if (user) {
            fetchMentors();
        }
    }, [selectedDiscipline, user]);

    const fetchDisciplines = async () => {
        try {
            const disciplinesRef = collection(db, 'disciplines');
            const disciplinesSnapshot = await getDocs(disciplinesRef);
            const disciplinesList = disciplinesSnapshot.docs.map(doc => doc.data().name);
            setDisciplines(disciplinesList.length ? disciplinesList : ['Civil', 'Mechanical', 'Electrical', 'Chemical', 'Industrial']);
        } catch (error) {
            console.error('Failed to fetch disciplines:', error);
            setDisciplines(['Civil', 'Mechanical', 'Electrical', 'Chemical', 'Industrial']);
        }
    };

    const fetchMentors = async () => {
        if (!user) return;

        setLoading(true);
        try {
            // Fetch all users with role 'mentor'
            const usersRef = collection(db, 'users');
            let mentorsQuery = query(usersRef, where('role', '==', 'mentor'), where('is_active', '==', true));

            const mentorsSnapshot = await getDocs(mentorsQuery);
            let mentorsList = [];

            for (const doc of mentorsSnapshot.docs) {
                const mentorData = doc.data();

                // Filter by discipline if selected
                if (selectedDiscipline && mentorData.discipline !== selectedDiscipline) {
                    continue;
                }

                // Fetch mentor profile (if separate collection exists)
                let mentorProfile = {};
                const profileRef = collection(db, 'mentorProfiles');
                const profileQuery = query(profileRef, where('userId', '==', doc.id));
                const profileSnapshot = await getDocs(profileQuery);

                if (!profileSnapshot.empty) {
                    mentorProfile = profileSnapshot.docs[0].data();
                }

                mentorsList.push({
                    id: doc.id,
                    ...mentorData,
                    profile: mentorProfile
                });
            }

            // Sort by match score (highest first)
            const mentorsWithScores = mentorsList.map(mentor => ({
                ...mentor,
                matchScore: calculateMatchScore(userData, mentor)
            }));

            mentorsWithScores.sort((a, b) => b.matchScore - a.matchScore);
            setMentors(mentorsWithScores);
        } catch (error) {
            console.error('Error fetching mentors:', error);
            toast.error('Failed to load mentors. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const calculateMatchScore = (candidateData, mentorData) => {
        if (!candidateData || !mentorData) return 0;

        let score = 0;
        let factors = 0;

        // Discipline match (40% weight)
        if (candidateData.discipline && mentorData.discipline) {
            if (candidateData.discipline === mentorData.discipline) {
                score += 40;
            } else if (candidateData.discipline.includes(mentorData.discipline) || mentorData.discipline.includes(candidateData.discipline)) {
                score += 20; // Partial match
            }
            factors += 40;
        }

        // Years of experience (30% weight)
        if (candidateData.years_of_experience && mentorData.years_of_experience) {
            const diff = mentorData.years_of_experience - candidateData.years_of_experience;
            if (diff >= 5) {
                score += 30;
            } else if (diff >= 2) {
                score += 20;
            } else if (diff > 0) {
                score += 10;
            }
            factors += 30;
        }

        // Specializations match (30% weight)
        if (candidateData.specializations && mentorData.specializations) {
            const commonSpecs = candidateData.specializations.filter(spec =>
                mentorData.specializations.includes(spec)
            );
            const matchPercentage = (commonSpecs.length / Math.max(candidateData.specializations.length, mentorData.specializations.length)) * 30;
            score += matchPercentage;
            factors += 30;
        }

        return factors > 0 ? Math.round((score / factors) * 100) : 50;
    };

    const handleRequestMentorship = async (mentorId) => {
        if (!user) {
            toast.error('Please login to request mentorship');
            return;
        }

        setRequestingMentor(mentorId);

        try {
            // Check if a request already exists
            const requestsRef = collection(db, 'mentorshipRequests');
            const existingQuery = query(
                requestsRef,
                where('candidateId', '==', user.uid),
                where('mentorId', '==', mentorId),
                where('status', 'in', ['pending', 'accepted'])
            );
            const existingSnapshot = await getDocs(existingQuery);

            if (!existingSnapshot.empty) {
                toast.warning('You already have a pending or accepted mentorship request with this mentor');
                return;
            }

            // Create mentorship request
            const requestData = {
                candidateId: user.uid,
                mentorId: mentorId,
                candidateName: userData?.full_name || user.displayName || 'Candidate',
                mentorName: mentors.find(m => m.id === mentorId)?.full_name || 'Mentor',
                message: `I would like to request your mentorship. I am a ${userData?.discipline || 'engineering'} professional with ${userData?.years_of_experience || 0} years of experience.`,
                status: 'pending',
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
                candidateDiscipline: userData?.discipline || null,
                candidateExperience: userData?.years_of_experience || null
            };

            await addDoc(collection(db, 'mentorshipRequests'), requestData);

            // Optionally send notification (you can implement Firebase Cloud Messaging)
            toast.success('Mentorship request sent successfully!');

            // Refresh mentors list to update UI if needed
            fetchMentors();
        } catch (error) {
            console.error('Error sending mentorship request:', error);
            toast.error('Failed to send mentorship request. Please try again.');
        } finally {
            setRequestingMentor(null);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center">
                <Card className="swiss-card p-8 text-center max-w-md">
                    <Users size={48} weight="duotone" className="text-[#666666] mx-auto mb-4" />
                    <h2 className="font-heading text-xl font-semibold text-[#0A0A0A] mb-2">Please Login</h2>
                    <p className="text-[#666666] mb-4">You need to be logged in to view mentors.</p>
                    <Button onClick={() => navigate('/')} className="bg-[#002FA7] text-white">
                        Go to Login
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F9F6]">
            <nav className="sticky-nav bg-white border-b border-[#DCDCDA]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Button
                            variant="ghost"
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-btn"
                            className="flex items-center gap-2 hover:bg-gray-100"
                        >
                            <ArrowLeft size={18} /> Back to Dashboard
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-8">
                    <h1 className="font-heading text-4xl font-semibold text-[#0A0A0A] mb-3">Find a Mentor</h1>
                    <p className="text-base text-[#666666]">Connect with experienced professionals in your engineering discipline.</p>
                </div>

                <div className="mb-8">
                    <div className="flex items-center gap-2">
                        <Filter size={20} className="text-[#666666]" />
                        <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
                            <SelectTrigger data-testid="filter-discipline" className="w-64 rounded-sm border-[#DCDCDA] bg-white">
                                <SelectValue placeholder="All Disciplines" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All Disciplines</SelectItem>
                                {disciplines.map(disc => (
                                    <SelectItem key={disc} value={disc}>{disc}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="text-center">
                            <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto"></div>
                            <p className="mt-4 text-[#666666]">Loading mentors...</p>
                        </div>
                    </div>
                ) : mentors.length === 0 ? (
                    <Card className="swiss-card p-12 text-center">
                        <Users size={48} weight="duotone" className="text-[#666666] mx-auto mb-4" />
                        <p className="text-base text-[#666666]">No mentors available for the selected discipline.</p>
                        {selectedDiscipline && (
                            <Button
                                onClick={() => setSelectedDiscipline('')}
                                variant="outline"
                                className="mt-4"
                            >
                                Clear Filter
                            </Button>
                        )}
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {mentors.map((mentor) => (
                            <Card key={mentor.id} className="mentor-card hover:shadow-lg transition-shadow" data-testid={`mentor-${mentor.id}`}>
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-1">
                                            {mentor.full_name || mentor.displayName || 'Mentor'}
                                        </h3>
                                        <p className="text-sm text-[#666666]">{mentor.discipline || 'Discipline not specified'}</p>
                                    </div>
                                    <div className="flex items-center gap-1 bg-[#FFB800]/10 px-2 py-1 rounded-sm">
                                        <Sparkle size={16} weight="fill" className="text-[#FFB800]" />
                                        <span className="font-mono text-sm font-semibold text-[#0A0A0A]">{mentor.matchScore}%</span>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <p className="text-sm text-[#666666] mb-2">
                                        {mentor.years_of_experience || 0} years experience
                                    </p>
                                    {mentor.bio && (
                                        <p className="text-sm text-[#666666] line-clamp-3">{mentor.bio}</p>
                                    )}
                                    {mentor.profile?.bio && !mentor.bio && (
                                        <p className="text-sm text-[#666666] line-clamp-3">{mentor.profile.bio}</p>
                                    )}
                                </div>

                                {mentor.specializations && mentor.specializations.length > 0 && (
                                    <div className="mb-4 flex flex-wrap gap-2">
                                        {mentor.specializations.slice(0, 3).map((spec, idx) => (
                                            <span key={idx} className="bg-[#002FA7]/10 text-[#002FA7] px-2 py-1 rounded-sm text-xs">
                                                {spec}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <Button
                                    onClick={() => handleRequestMentorship(mentor.id)}
                                    disabled={requestingMentor === mentor.id}
                                    data-testid={`request-btn-${mentor.id}`}
                                    className="w-full bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <PaperPlaneTilt size={16} />
                                    {requestingMentor === mentor.id ? 'Sending...' : 'Request Mentorship'}
                                </Button>
                            </Card>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default MentorsPage;